import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, MessageCircle, ArrowRight, Calendar } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/shared/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { createLead, formatApiErrorDetail } from "@/lib/api";
import { COMPANY } from "@/data/site";

const FIELD =
  "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[15px] text-[#111111] placeholder:text-gray-400 focus:outline-none focus:border-[#0B63F6] focus:ring-1 focus:ring-[#0B63F6] transition-colors";

const localBusiness = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "CompuBrain AI",
  image: COMPANY.logo,
  "@id": "https://compubrain.ai/#localbusiness",
  url: "https://compubrain.ai",
  telephone: COMPANY.phone,
  email: COMPANY.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: "CompuBrain House, Anjali Society #8, Maninagar",
    addressLocality: "Ahmedabad",
    addressRegion: "Gujarat",
    postalCode: "380008",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: COMPANY.geo.lat, longitude: COMPANY.geo.lng },
  openingHours: "Mo-Sa 10:00-19:00",
  areaServed: "Worldwide",
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please share your name and email.");
    setSubmitting(true);
    try {
      await createLead({ ...form, source: "contact-page", service_interest: "General Enquiry" });
      toast.success("Thank you — our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const mapSrc = `https://maps.google.com/maps?q=${COMPANY.geo.lat},${COMPANY.geo.lng}&z=16&output=embed`;

  return (
    <>
      <Seo
        title="Contact CompuBrain AI — Talk to a GEO Expert"
        description="Contact CompuBrain AI in Ahmedabad, India to discuss Generative Engine Optimization. Request an AI visibility audit, schedule a strategy call or talk to a GEO expert."
        path="/contact"
        schemas={[localBusiness, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <PageHero
        eyebrow="Contact"
        titleLines={["Talk to a", "GEO expert."]}
        sub="Tell us about your business and goals. We'll respond with tailored next steps for winning AI visibility."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact" }]}
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="rounded-2xl border border-gray-200 p-8 lg:p-10" data-testid="contact-form">
                <h2 className="font-display text-2xl font-bold tracking-tight">Send an enquiry</h2>
                <p className="text-gray-600 mt-2 text-[15px]">We typically respond within one business day.</p>
                <div className="mt-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={FIELD} placeholder="Full name*" value={form.name} onChange={set("name")} data-testid="contact-name" />
                    <input className={FIELD} type="email" placeholder="Work email*" value={form.email} onChange={set("email")} data-testid="contact-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={FIELD} placeholder="Company" value={form.company} onChange={set("company")} data-testid="contact-company" />
                    <input className={FIELD} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                  </div>
                  <textarea className={`${FIELD} min-h-[130px] resize-none`} placeholder="How can we help with your AI visibility?" value={form.message} onChange={set("message")} data-testid="contact-message" />
                  <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#0B63F6] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#6E56CF] transition-colors disabled:opacity-60">
                    {submitting ? "Sending..." : "Submit enquiry"} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-6 rounded-2xl border border-dashed border-gray-300 p-6 flex items-center gap-4" data-testid="calendly-placeholder">
                <span className="h-11 w-11 rounded-xl bg-[#0B63F6]/[0.08] flex items-center justify-center text-[#0B63F6]"><Calendar className="h-5 w-5" /></span>
                <div>
                  <div className="font-display font-bold">Prefer to book a call?</div>
                  <p className="text-sm text-gray-600">Calendly scheduling integration placeholder — connect your calendar to enable instant booking.</p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 space-y-5">
            <Reveal>
              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <iframe title="CompuBrain AI office location" src={mapSrc} width="100%" height="240" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="google-map" />
              </div>
            </Reveal>
            {[
              { icon: MapPin, label: "Head Office", value: COMPANY.legalAddress },
              { icon: Phone, label: "Phone", value: COMPANY.phone, href: COMPANY.phoneHref },
              { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: Clock, label: "Business Hours", value: COMPANY.hours },
            ].map((row) => (
              <Reveal key={row.label} delay={0.04}>
                <div className="flex items-start gap-4 rounded-2xl border border-gray-200 p-5">
                  <span className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center text-[#111111] shrink-0"><row.icon className="h-5 w-5" /></span>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">{row.label}</div>
                    {row.href ? <a href={row.href} className="text-[15px] text-[#111111] hover:text-[#0B63F6] transition-colors">{row.value}</a> : <div className="text-[15px] text-[#111111]">{row.value}</div>}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta" className="flex items-center justify-center gap-2 rounded-full bg-[#19A974] text-white px-7 py-3.5 font-medium hover:bg-[#128a5f] transition-colors">
                <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
